import React, { useEffect, useRef, useState } from 'react';
import '../styles/InvestmentProducts.css';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ScrollAnimation from '../components/ScrollAnimation';

const InvestmentProducts: React.FC = () => {
    const { t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [searchParams] = useSearchParams();
    const tileRefs = useRef<(HTMLDivElement | null)[]>([]);

    const getTileIndex = (section: string) => {
        switch (section) {
            case 'products': return 'header'; // Special case for main header
            case 'acquisition': return 0;
            case 'management': return 1;
            case 'exit': return 2;
            default: return 0;
        }
    };
    const tileSets = [
        {
            id: 1,
            title: t('investProdPageInvestmProd1Title'),
            subtitle: t('investProdPageInvestmProd1Subtitle'),
            description: t('investProdPageInvestmProd1Desc'),
            additionalInformation: "",
            buttonText: t('investProdPageInvestmProd1Button')
        },
        {
            id: 2,
            title: t('investProdPageInvestmProd3Title'),
            subtitle: t('investProdPageInvestmProd3Subtitle'),
            description: t('investProdPageInvestmProd3Desc'),
            additionalInformation: t('investProdPageInvestmProd3AddInfo'),
            buttonText: t('investProdPageInvestmProd3Button')
        },

    ];

    useEffect(() => {
        const section = searchParams.get('section');
        if (section !== null && section !== 'top') {
            // Scroll to top first
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Then scroll to specific section after a short delay
            setTimeout(() => {
                const tileIndex = getTileIndex(section);
                
                if (tileIndex === 'header') {
                    // Special case: scroll to the main header (investProdPageInvestmProdH1)
                    const headerElement = document.querySelector('.section-investmentProducts h1') as HTMLElement;
                    if (headerElement) {
                        const elementPosition = headerElement.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset;
                        
                        // Calculate responsive offset based on screen size
                        let headerOffset = 120; // Default offset for larger screens
                        
                        if (window.innerWidth <= 1440) { // Laptop screens and smaller
                            headerOffset = 180; // Larger offset for laptop screens
                        }
                        
                        if (window.innerWidth <= 768) { // Mobile screens
                            headerOffset = 100; // Smaller offset for mobile
                        }
                        
                        window.scrollTo({
                            top: offsetPosition - headerOffset,
                            behavior: 'smooth'
                        });
                    }
                } else {
                    // Regular tile scrolling
                    const element = tileRefs.current[tileIndex as number];
                    if (element) {
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset;
                        
                        // Calculate responsive offset based on screen size
                        let headerOffset = 120; // Default offset for larger screens
                        
                        if (window.innerWidth <= 1440) { // Laptop screens and smaller
                            headerOffset = 180; // Larger offset for laptop screens
                        }
                        
                        if (window.innerWidth <= 768) { // Mobile screens
                            headerOffset = 100; // Smaller offset for mobile
                        }
                        
                        window.scrollTo({
                            top: offsetPosition - headerOffset,
                            behavior: 'smooth'
                        });
                    }
                }
            }, 200); // Increased delay for better reliability
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [searchParams]);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % tileSets.length);
        }, 8000);
        return () => clearInterval(timer);
    }, [tileSets.length]);

    return (
        <div className='container'>
            <section className='investment-header-section page-hero'> </section>
            <section className='main-section'>
                <ScrollAnimation animation="fadeInUp" delay={0.1}>
                    <h1>{t('investProdPageBelowHeroH1')}</h1>
                    <h2>{t('investProdPageBelowHeroH2')}</h2>
                    <p>{t('investProdPageBelowHeroP1')}
                        <br />
                        <br />{t('investProdPageBelowHeroP3')}</p>
                </ScrollAnimation>
            </section>
            <section className="hero-section section-investmentProducts">
                <div className='slider-section investment-slider-section'>
                    <ScrollAnimation animation="fadeInUp" delay={0.1}>
                        <h1>{t('investProdPageInvestmProdH1')}</h1>
                        <h2>{t('investProdPageInvestmProdH2')}</h2>
                        <p>{t('investProdPageInvestmProdP')}</p>
                    </ScrollAnimation>
                    <section className="hero-section">
                        <div className="process-section">
                            <div className="container">
                                <div className="process-tiles">
                                    {tileSets.map((step, index) => (
                                        <ScrollAnimation animation="fadeInUp" delay={index * 0.1}>
                                            <div
                                                key={step.id}
                                                ref={(el) => { tileRefs.current[index] = el; }}
                                                className="process-tile"
                                            >
                                            <div className="tile-content">
                                                <h3 className="tile-title investment-tile">{step.title}</h3>
                                                <h4 className="tile-subtitle">{step.subtitle}</h4>
                                                <p className='tile-description'>{step.description}</p>
                                                <div className="additional-information">
                                                    {step.additionalInformation.split('\n\n').map((paragraph: string, idx: number) => (
                                                        <div key={idx}>
                                                            <p>{paragraph}</p>
                                                            {idx < step.additionalInformation.split('\n\n').length - 1 && (
                                                                <hr className="paragraph-divider" />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className='referenceButton'>
                                                    <Link to="/kontakt" className="tile-button">{step.buttonText}</Link>
                                                </div>
                                            </div>
                                            <div className="tile-number">{step.id}</div>
                                        </div>
                                        </ScrollAnimation>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </section>
            <section className='main-section references-contact'>
                <ScrollAnimation animation="fadeInUp" delay={0.1}>
                    <h1 className="header-contact">{t('investProdPagePartner?H1')}</h1>
                    <h2>{t('investProdPagePartner?H2')}</h2>
                    <div className="contact-button-section">
                        <Link to='/kontakt' className="knowhow-button-section">{t('investProdPagePartnerKontakt')}</Link>
                    </div>
                </ScrollAnimation>
            </section>
        </div>
    );
};

export default InvestmentProducts;